import { createImageComponent } from "./createImageComponent.ts";
import { createImageSrcBuilder } from "./createImageSrc.ts";
import type { ImagePath as HomeImagePath, ImageThumbhashes as HomeImageThumbhashes } from "./generated/home.ts";
import type {
  ImagePath as StudentImagePath,
  ImageThumbhashes as StudentImageThumbhashes,
} from "./generated/student.ts";
import type { ImagePath as TeachImagePath, ImageThumbhashes as TeachImageThumbhashes } from "./generated/teach.ts";
import type {
  ImagePath as ToolkitImagePath,
  ImageThumbhashes as ToolkitImageThumbhashes,
} from "./generated/toolkit.ts";
import type { ImagePath as WebImagePath, ImageThumbhashes as WebImageThumbhashes } from "./generated/web.ts";

// The one <Image>/getImageSrc every app imports, so a component shared across apps (in web/ or
// packages/molecules) doesn't need to know which app it's rendering in, and a component that
// happens to reach across app boundaries (five call sites already did, before this file existed —
// teach and student both render web's assets) just works instead of importing a second module.
//
// Safe because bundling changed nothing here: an image path used to be an import, so referencing
// another app's asset meant bundling its bytes into yours. It's a bare string now, resolved
// against a public CDN at runtime — every bucket is reachable from every app, so there was never a
// real boundary left to enforce, only a fictional one in the types.
//
// Every app's generator emits its ImagePath union with its own bucket segment already folded in as
// the first path component ("teach/app/...", "web/...", "home/...", "student/...",
// "toolkit/src/pods/...", "toolkit/public/static/images/..." — see bucketSegmentFor in
// generateImageThumbhashes.ts), so this file only has to union these types and pick one base URL;
// there's no per-app table anywhere in the resolution path itself.
export type ImagePath = TeachImagePath | WebImagePath | HomeImagePath | StudentImagePath | ToolkitImagePath;

// Disjoint by construction (every key already carries its own app's segment), so this intersection
// is just "all key sets, each interface only ever satisfying its own keys" — not a case where a
// real property collision could silently pick the wrong hash.
type ImageThumbhashes = TeachImageThumbhashes &
  WebImageThumbhashes &
  HomeImageThumbhashes &
  StudentImageThumbhashes &
  ToolkitImageThumbhashes;

// No app segment here — it's already the first path component of every ImagePath value, so this
// is the shared bucket root, not one app's own subtree the way each app's old *_IMAGES_BASE_URL
// constant was.
const ALL_IMAGES_BASE_URL = "https://static.classdojo.com/app-images";

const { getImageSrc: resolveImageSrc } = createImageSrcBuilder(ALL_IMAGES_BASE_URL);

// A function accepting `string` already satisfies `(path: ImagePath) => string` — parameter types
// are contravariant, so this narrowing needs only an annotation, not an `as` cast.
export const getImageSrc: (path: ImagePath) => string = resolveImageSrc;

export const Image = createImageComponent<ImageThumbhashes, ImagePath>(resolveImageSrc);
